
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Globe } from "lucide-react";
import { useLanguage } from "@/components/LanguageContext";

interface ApproveScheduleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  event: any;
  onEventUpdated: () => void;
}

export const ApproveScheduleDialog = ({
  isOpen,
  onClose,
  event,
  onEventUpdated
}: ApproveScheduleDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [sessionDuration, setSessionDuration] = useState("60");
  const [meetingUrl, setMeetingUrl] = useState("");
  const { language } = useLanguage();
  
  if (!event) return null;

  const isOnlineRequest = event.request_online || false;

  const handleApprove = async () => {
    setLoading(true);
    try {
      // Validate meeting URL for online sessions
      if (isOnlineRequest && !meetingUrl) {
        toast.error(language === 'pt' ? 'Por favor, forneça um link para a aula online' : 'Please provide a link for the online class');
        setLoading(false);
        return;
      }
      
      // 1. Update the request status to approved
      const { error: requestError } = await supabase
        .from('schedule_requests')
        .update({ status: 'approved' })
        .eq('id', event.id);
        
      if (requestError) throw requestError;

      // 2. Find any placeholder event that was created for this request
      const { data: placeholderEvents, error: placeholderError } = await supabase
        .from('schedule_events')
        .select('*')
        .eq('related_id', event.id);
        
      if (placeholderError) {
        console.error('Error finding placeholder events:', placeholderError);
        // Continue anyway, we'll create a new event
      }

      // Calculate end time based on duration
      const startDateTime = new Date(event.start_time || event.requested_date);
      const endDateTime = new Date(startDateTime.getTime() + parseInt(sessionDuration) * 60 * 1000);

      // 3. If placeholder event exists, update it
      if (placeholderEvents && placeholderEvents.length > 0) {
        const { error: updateError } = await supabase
          .from('schedule_events')
          .update({
            title: isOnlineRequest ? 
                  (language === 'pt' ? 'Aula Online' : 'Online Class') : 
                  (language === 'pt' ? 'Aula' : 'Class'),
            event_type: isOnlineRequest ? 'online_session' : 'training_session',
            status: 'scheduled',
            end_time: endDateTime.toISOString(),
            is_online: isOnlineRequest,
            meeting_url: isOnlineRequest ? meetingUrl : null
          })
          .eq('id', placeholderEvents[0].id);
          
        if (updateError) {
          console.error('Error updating placeholder event:', updateError);
          // Continue anyway, we might create a new event
        } else {
          // Success! No need to create a new event
          toast.success(language === 'pt' ? 'Pedido aprovado com sucesso' : 'Request approved successfully');
          onEventUpdated();
          onClose();
          return;
        }
      }
      
      // 4. If no placeholder event or update failed, create a new event
      const { error: createError } = await supabase
        .from('schedule_events')
        .insert({
          trainer_id: event.trainer_id,
          student_id: event.student_id,
          title: isOnlineRequest ? 
                (language === 'pt' ? 'Aula Online' : 'Online Class') : 
                (language === 'pt' ? 'Aula' : 'Class'),
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          event_type: isOnlineRequest ? 'online_session' : 'training_session',
          status: 'scheduled',
          related_id: event.id, // Link to the request
          is_online: isOnlineRequest,
          meeting_url: isOnlineRequest ? meetingUrl : null
        });
        
      if (createError) throw createError;
      
      toast.success(language === 'pt' ? 'Pedido aprovado com sucesso' : 'Request approved successfully');
      onEventUpdated();
      onClose();
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error(language === 'pt' ? 'Erro ao aprovar pedido' : 'Error approving request');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string) => {
    try {
      return format(new Date(date), "dd 'de' MMMM 'às' HH:mm", { locale: pt });
    } catch (e) {
      return date;
    }
  };

  const studentName = event.students ? 
    `${event.students.first_name} ${event.students.last_name}` : 
    (event.student ? `${event.student.first_name} ${event.student.last_name}` : 'Student');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="dropdown-opaque">
        <DialogHeader>
          <DialogTitle>{language === 'pt' ? 'Aprovar Pedido' : 'Approve Request'}</DialogTitle>
          <DialogDescription>
            {language === 'pt' ? 'Aprovar pedido de aula para' : 'Approve class request for'} {studentName}
            {isOnlineRequest && (
              <span className="flex items-center gap-2 mt-2 text-blue-500">
                <Globe className="h-4 w-4" />
                {language === 'pt' ? '(Aula Online)' : '(Online Class)'}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 my-4">
          <div>
            <span className="font-medium">{language === 'pt' ? 'Data:' : 'Date:'}</span>{" "}
            {formatDate(event.start_time || event.requested_date)}
          </div>
          
          {isOnlineRequest && (
            <div className="space-y-2">
              <Label htmlFor="meetingUrl">
                {language === 'pt' ? 'URL da reunião (Zoom, Google Meet, etc.)' : 'Meeting URL (Zoom, Google Meet, etc.)'}
              </Label>
              <Input
                id="meetingUrl"
                value={meetingUrl}
                onChange={(e) => setMeetingUrl(e.target.value)}
                placeholder="https://zoom.us/j/123456789"
                className="bg-white dark:bg-gray-800"
              />
            </div>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="duration">{language === 'pt' ? 'Duração (minutos)' : 'Duration (minutes)'}</Label>
            <Select value={sessionDuration} onValueChange={setSessionDuration}>
              <SelectTrigger id="duration">
                <SelectValue placeholder={language === 'pt' ? 'Selecione a duração' : 'Select duration'} />
              </SelectTrigger>
              <SelectContent className="dropdown-opaque">
                <SelectItem value="30">{language === 'pt' ? '30 minutos' : '30 minutes'}</SelectItem>
                <SelectItem value="45">{language === 'pt' ? '45 minutos' : '45 minutes'}</SelectItem>
                <SelectItem value="60">{language === 'pt' ? '1 hora' : '1 hour'}</SelectItem>
                <SelectItem value="90">{language === 'pt' ? '1 hora e 30 minutos' : '1 hour and 30 minutes'}</SelectItem>
                <SelectItem value="120">{language === 'pt' ? '2 horas' : '2 hours'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {language === 'pt' ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button 
            onClick={handleApprove} 
            disabled={loading || (isOnlineRequest && !meetingUrl)}
            className="bg-green-600 hover:bg-green-700"
          >
            {loading 
              ? (language === 'pt' ? 'Processando...' : 'Processing...') 
              : (language === 'pt' ? 'Aprovar Aula' : 'Approve Class')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
